import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { formMasterAPI, commonAPI } from '../../services/api';
import toast from 'react-hot-toast';
import Modal from '../../components/common/Modal';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { format } from 'date-fns';

const ClassStudents = () => {
  const [searchParams] = useSearchParams();
  const [classId, setClassId] = useState(searchParams.get('class') || '');
  const [academicSessions, setAcademicSessions] = useState([]);
  const [terms, setTerms] = useState([]);
  const [selectedSession, setSelectedSession] = useState('');
  const [selectedTerm, setSelectedTerm] = useState('');
  const [students, setStudents] = useState([]);
  const [myClasses, setMyClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [formKey, setFormKey] = useState(Date.now());
  const [isModalLoading, setIsModalLoading] = useState(false);
  
  // Comment fields structure
  const [commentFields, setCommentFields] = useState({
    psychomotor: [],
    affective: [],
    custom: []
  });
  
  // Existing comment structure
  const [existingComment, setExistingComment] = useState({
    comment: '',
    psycho_motor: {},
    affective: {},
    custom_ratings: {},
    has_comment: false,
    has_psychomotor: false,
    has_affective: false,
    has_custom: false
  });

  // ✅ FIXED: Complete validation schema
  const commentSchema = Yup.object().shape({
    comment: Yup.string().required('Comment is required')
    // Ratings are optional
  });

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (classId && selectedSession && selectedTerm) {
      fetchClassStudents();
    }
  }, [classId, selectedSession, selectedTerm]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      
      const classesRes = await formMasterAPI.getMyClasses();
      const sessionsRes = await formMasterAPI.getAcademicSessions();

      const classesData = classesRes?.data || classesRes || [];
      const sessionsData = sessionsRes?.data || sessionsRes || [];

      setMyClasses(Array.isArray(classesData) ? classesData : []);
      setAcademicSessions(Array.isArray(sessionsData) ? sessionsData : []);

      if (Array.isArray(classesData) && classesData.length > 0 && !classId) {
        setClassId(classesData[0].class_id);
      }
      
      if (Array.isArray(sessionsData) && sessionsData.length > 0 && !selectedSession) {
        setSelectedSession(sessionsData[0].id);
      }

    } catch (error) {
      console.error('Error in fetchInitialData:', error);
      toast.error('Failed to load initial data');
    } finally {
      setLoading(false);
    }
  };

  const fetchClassStudents = async () => {
    if (!classId || !selectedSession || !selectedTerm) {
      toast.error('Please select class, session, and term');
      return;
    }
    
    try {
      setLoading(true);
      
      const response = await formMasterAPI.getClassStudents({
        class_id: classId,
        academic_session_id: selectedSession,
        term_id: selectedTerm
      });
      
      const studentsData = response?.data?.students || response?.students || [];
      
      const studentsWithCommentStatus = await Promise.all(
        studentsData.map(async (student) => {
          try {
            const commentRes = await formMasterAPI.getStudentComment(
              student.student_reg,
              classId,
              selectedSession,
              selectedTerm
            );
            
            return {
              ...student,
              has_comment: commentRes?.data?.has_comment || false
            };
          } catch {
            return { ...student, has_comment: false };
          }
        })
      );
      
      setStudents(studentsWithCommentStatus);
      
    } catch (error) {
      console.error('Failed to load class students:', error);
      toast.error(error.response?.data?.detail || 'Failed to load class students');
    } finally {
      setLoading(false);
    }
  };

  const handleSessionChange = async (sessionId) => {
    setSelectedSession(sessionId);
    setSelectedTerm('');
    
    try {
      const selectedSessionData = academicSessions.find(s => s.id == sessionId);
      if (selectedSessionData?.terms) {
        setTerms(selectedSessionData.terms);
        if (selectedSessionData.terms.length > 0) {
          setSelectedTerm(selectedSessionData.terms[0].id);
        }
      } else {
        const response = await commonAPI.getTerms(sessionId);
        const termsData = response?.data || response || [];
        setTerms(termsData);
        if (termsData.length > 0) {
          setSelectedTerm(termsData[0].id);
        }
      }
    } catch (error) {
      console.error('Failed to load terms:', error);
      toast.error('Failed to load terms');
    }
  };

  const loadCommentFields = async () => {
    try {
      const response = await formMasterAPI.getCommentFields();
      const data = response?.data || response || {};
      
      setCommentFields({
        psychomotor: data.psychomotor || data.PSYCHOMOTOR || [],
        affective: data.affective || data.AFFECTIVE || [],
        custom: data.custom || data.CUSTOM || []
      });
    } catch (error) {
      console.error('Failed to load comment fields:', error);
    }
  };

  // ✅ FIXED: Accept student as parameter to avoid race condition
  const loadExistingComment = async (student) => {
    if (!student || !classId || !selectedSession || !selectedTerm) return;
    
    try {
      const response = await formMasterAPI.getStudentComment(
        student.student_reg,
        classId,
        selectedSession,
        selectedTerm
      );
      
      const commentData = response?.data || response || {};
      
      setExistingComment({
        comment: commentData.comment || '',
        psycho_motor: commentData.psycho_motor || {},
        affective: commentData.affective || {},
        custom_ratings: commentData.custom_ratings || {},
        has_comment: !!commentData.has_comment,
        has_psychomotor: !!commentData.has_psychomotor,
        has_affective: !!commentData.has_affective,
        has_custom: !!commentData.has_custom
      });
    } catch (error) {
      console.log('No existing comment (new student)');
      // Don't show error toast as it's expected for new comments
    }
  };

  // ✅ FIXED: Properly sequenced loading to avoid race conditions
  const openCommentModal = async (student) => {
    setShowCommentModal(true);
    setIsModalLoading(true);
    setSelectedStudent(student);
    
    // Reset states first
    setExistingComment({
      comment: '',
      psycho_motor: {},
      affective: {},
      custom_ratings: {},
      has_comment: false,
      has_psychomotor: false,
      has_affective: false,
      has_custom: false
    });
    
    setCommentFields({
      psychomotor: [],
      affective: [],
      custom: []
    });
    
    // Load comment data using the student directly (no race condition)
    await loadCommentFields();
    await loadExistingComment(student);
    
    setIsModalLoading(false);
    setFormKey(Date.now()); // Force Formik reinitialization
  };

  // ✅ FIXED: Proper handleSaveComment function with form submission fix
  const handleSaveComment = async (values, { setSubmitting }) => {
    if (!selectedStudent || !classId || !selectedSession || !selectedTerm) {
      toast.error('Missing required information');
      setSubmitting(false);
      return;
    }
    
    try {
      // Prepare data exactly as backend expects
      const commentData = {
        student_reg: selectedStudent.student_reg,
        class_id: classId,
        academic_session_id: selectedSession,
        term_id: selectedTerm,
        comment: values.comment,
        psycho_motor: values.psycho_motor || {},
        affective: values.affective || {},
        custom_ratings: values.custom_ratings || {}
      };

      console.log('Saving comment data:', commentData);

      const response = await formMasterAPI.addComment(commentData);
      
      if (response?.data?.message) {
        toast.success(response.data.message);
      } else {
        toast.success('Comment and ratings saved successfully');
      }
      
      // Refresh the student list to update comment status
      await fetchClassStudents();
      
      setShowCommentModal(false);
      setSelectedStudent(null);
      
      // Clear existing comment
      setExistingComment({
        comment: '',
        psycho_motor: {},
        affective: {},
        custom_ratings: {},
        has_comment: false,
        has_psychomotor: false,
        has_affective: false,
        has_custom: false
      });
      
    } catch (error) {
      console.error('Failed to save comment:', error);
      toast.error(error.response?.data?.detail || error.response?.data?.message || 'Failed to save comment');
    } finally {
      setSubmitting(false);
    }
  };

  // ✅ FIXED: Star rating component with proper form integration
  const renderRatingField = (field, currentValue, onChange) => {
    const maxScore = field.max_score || 5;
    const minScore = field.min_score || 1;
    const value = currentValue || 0;
    
    return (
      <div className="mb-4" key={field.field_code}>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {field.display_name}
        </label>
        <div className="flex items-center space-x-2">
          <div className="flex space-x-1">
            {Array.from({ length: maxScore }, (_, i) => i + minScore).map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => onChange(field.field_code, star)}
                className={`text-2xl ${star <= value ? 'text-yellow-500' : 'text-gray-300'} hover:text-yellow-400 transition-colors`}
              >
                ★
              </button>
            ))}
          </div>
          <span className="text-sm text-gray-600 ml-2">
            {value > 0 ? `${value}/${maxScore}` : 'Not rated'}
          </span>
        </div>
      </div>
    );
  };

  const getCurrentClass = () => {
    return myClasses.find(cls => cls.class_id == classId);
  };

  // ✅ FIXED: Dynamic getInitialValues function that properly maps existing data
  const getInitialValues = () => {
    // Only build initial values when we have both fields and existing data
    if (commentFields.psychomotor.length === 0 && 
        commentFields.affective.length === 0 && 
        commentFields.custom.length === 0) {
      return {
        comment: '',
        psycho_motor: {},
        affective: {},
        custom_ratings: {}
      };
    }
    
    // Build psychomotor ratings - dynamically from backend fields
    const psycho_motor = {};
    if (commentFields.psychomotor && commentFields.psychomotor.length > 0) {
      commentFields.psychomotor.forEach(field => {
        const fieldCode = field.field_code;
        // Check if this field exists in the saved data, otherwise use 0
        if (existingComment.psycho_motor && existingComment.psycho_motor[fieldCode] !== undefined) {
          psycho_motor[fieldCode] = existingComment.psycho_motor[fieldCode];
        } else {
          psycho_motor[fieldCode] = 0;
        }
      });
    }
    
    // Build affective ratings
    const affective = {};
    if (commentFields.affective && commentFields.affective.length > 0) {
      commentFields.affective.forEach(field => {
        const fieldCode = field.field_code;
        if (existingComment.affective && existingComment.affective[fieldCode] !== undefined) {
          affective[fieldCode] = existingComment.affective[fieldCode];
        } else {
          affective[fieldCode] = 0;
        }
      });
    }
    
    // Build custom ratings
    const custom_ratings = {};
    if (commentFields.custom && commentFields.custom.length > 0) {
      commentFields.custom.forEach(field => {
        const fieldCode = field.field_code;
        if (existingComment.custom_ratings && existingComment.custom_ratings[fieldCode] !== undefined) {
          custom_ratings[fieldCode] = existingComment.custom_ratings[fieldCode];
        } else {
          custom_ratings[fieldCode] = 0;
        }
      });
    }
    
    console.log('Building initial values:', {
      comment: existingComment.comment,
      psycho_motor,
      affective,
      custom_ratings
    });
    
    return {
      comment: existingComment.comment || '',
      psycho_motor,
      affective,
      custom_ratings
    };
  };

  if (loading && !students.length) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Class Students Management</h1>
          <p className="text-gray-600">View and manage students in your assigned classes</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => window.location.href = `/form-master/calculate-grades?class=${classId}&session=${selectedSession}&term=${selectedTerm}`}
            disabled={!classId || !selectedSession || !selectedTerm}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            <i className="fas fa-calculator mr-2"></i>
            Calculate Grades
          </button>
          <button
            onClick={() => window.location.href = `/form-master/reports?class=${classId}`}
            disabled={!classId}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            <i className="fas fa-chart-bar mr-2"></i>
            View Reports
          </button>
        </div>
      </div>

      {/* Class Selection and Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select Class *
            </label>
            <select
              value={classId}
              onChange={(e) => setClassId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={loading}
            >
              <option value="">Choose a class</option>
              {myClasses.map((cls) => (
                <option key={cls.class_id} value={cls.class_id}>
                  {cls.class_name} ({cls.student_count} students)
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Academic Session *
            </label>
            <select
              value={selectedSession}
              onChange={(e) => handleSessionChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={loading}
            >
              <option value="">Select Session</option>
              {academicSessions.map((session) => (
                <option key={session.id} value={session.id}>
                  {session.academic_session}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Term *
            </label>
            <select
              value={selectedTerm}
              onChange={(e) => setSelectedTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={!selectedSession || loading}
            >
              <option value="">Select Term</option>
              {terms.map((term) => (
                <option key={term.id} value={term.id}>
                  {term.term}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={fetchClassStudents}
              disabled={!classId || !selectedSession || !selectedTerm || loading}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  Loading...
                </>
              ) : (
                <>
                  <i className="fas fa-sync mr-2"></i>
                  Load Students
                </>
              )}
            </button>
          </div>
        </div>

        {classId && (
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
              <div>
                <p className="text-blue-700">
                  Viewing students for <strong className="font-semibold">{getCurrentClass()?.class_name || 'Selected Class'}</strong>
                  {selectedSession && selectedTerm && (
                    <span> for <strong className="font-semibold">
                      {academicSessions.find(s => s.id == selectedSession)?.academic_session || selectedSession} - 
                      {terms.find(t => t.id == selectedTerm)?.term || selectedTerm}
                    </strong></span>
                  )}
                </p>
              </div>
              <div className="text-sm text-blue-600 font-medium">
                {students.length} students found
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Students List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-6 border-b">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
            <h3 className="text-lg font-semibold text-gray-800">Students List</h3>
            <div className="text-sm text-gray-500">
              {students.length} students • Class: {getCurrentClass()?.class_name}
            </div>
          </div>
        </div>

        {students.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Student
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Personal Details
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Academic Info
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Comment Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {students.map((student) => (
                  <tr key={student.student_reg} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-green-100 rounded-full flex items-center justify-center">
                          <i className="fas fa-user-graduate text-green-600"></i>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {student.full_name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {student.student_reg}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <div className="text-sm">
                          <span className="text-gray-500">Gender: </span>
                          <span className="font-medium">{student.sex || 'N/A'}</span>
                        </div>
                        <div className="text-sm">
                          <span className="text-gray-500">DOB: </span>
                          <span className="font-medium">
                            {student.dob ? format(new Date(student.dob), 'dd/MM/yyyy') : 'N/A'}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <div className="text-sm">
                          <span className="text-gray-500">Enrolled: </span>
                          <span className="font-medium">
                            {student.created_at ? format(new Date(student.created_at), 'dd/MM/yyyy') : 'N/A'}
                          </span>
                        </div>
                        <div className="text-sm">
                          <span className="text-gray-500">Status: </span>
                          <span className={`font-medium ${student.application_status === 'approved' ? 'text-green-600' : 'text-yellow-600'}`}>
                            {student.application_status || 'pending'}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        student.application_status === 'approved' 
                          ? 'bg-green-100 text-green-800'
                          : student.application_status === 'pending'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {student.application_status || 'Pending'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {student.has_comment ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <i className="fas fa-check-circle mr-1"></i> Comment Added
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          <i className="fas fa-exclamation-circle mr-1"></i> No Comment
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm font-medium">
                      <button
                        onClick={() => openCommentModal(student)}
                        className="p-2 text-blue-600 hover:text-blue-900 rounded-full hover:bg-blue-50"
                        title="Add/Edit Comment"
                      >
                        <i className="fas fa-comment"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <i className="fas fa-users text-4xl text-gray-300 mb-4"></i>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Students Found</h3>
            <p className="text-gray-500 max-w-md mx-auto">
              {classId && selectedSession && selectedTerm
                ? 'No students are enrolled in this class for the selected academic session and term.'
                : 'Please select a class, academic session, and term to view students.'}
            </p>
          </div>
        )}
      </div>

      {/* Class Summary */}
      {students.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Class Summary</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="border border-gray-200 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-gray-900">{students.length}</div>
              <div className="text-sm text-gray-500">Total Students</div>
            </div>
            <div className="border border-gray-200 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-gray-900">
                {students.filter(s => s.sex === 'Male' || s.sex === 'male').length}
              </div>
              <div className="text-sm text-gray-500">Male Students</div>
            </div>
            <div className="border border-gray-200 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-gray-900">
                {students.filter(s => s.sex === 'Female' || s.sex === 'female').length}
              </div>
              <div className="text-sm text-gray-500">Female Students</div>
            </div>
            <div className="border border-gray-200 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-gray-900">
                {students.filter(s => s.application_status === 'approved').length}
              </div>
              <div className="text-sm text-gray-500">Approved</div>
            </div>
          </div>
        </div>
      )}

      {/* Comment Modal */}
      <Modal
        isOpen={showCommentModal}
        onClose={() => {
          setShowCommentModal(false);
          setSelectedStudent(null);
          setExistingComment({
            comment: '',
            psycho_motor: {},
            affective: {},
            custom_ratings: {},
            has_comment: false,
            has_psychomotor: false,
            has_affective: false,
            has_custom: false
          });
        }}
        title={`${existingComment.has_comment ? 'Edit' : 'Add'} Comment for ${selectedStudent?.full_name || 'Student'}`}
        size="xl"
      >
        {isModalLoading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <Formik
            key={formKey} // This forces Formik to reinitialize when data loads
            initialValues={getInitialValues()}
            validationSchema={commentSchema}
            onSubmit={handleSaveComment}
            enableReinitialize={true}
          >
            {({ isSubmitting, values, setFieldValue, handleSubmit, errors, touched }) => (
              <Form onSubmit={handleSubmit} className="space-y-6">
                {/* Student Info */}
                {selectedStudent && (
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                        <i className="fas fa-user-graduate text-blue-600 text-xl"></i>
                      </div>
                      <div>
                        <div className="font-bold text-gray-900 text-lg">{selectedStudent.full_name}</div>
                        <div className="text-sm text-gray-600">Registration: {selectedStudent.student_reg}</div>
                        <div className="text-sm text-gray-600">Class: {getCurrentClass()?.class_name}</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Existing Comment Status */}
                {existingComment.has_comment && (
                  <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                    <div className="flex items-center">
                      <i className="fas fa-info-circle text-green-600 mr-2"></i>
                      <span className="text-sm text-green-800">
                        You have previously saved a comment for this student. You can edit it below.
                      </span>
                    </div>
                  </div>
                )}

                {/* Overall Comment */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Overall Comment *
                  </label>
                  <Field
                    as="textarea"
                    name="comment"
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter your observations and feedback for this student..."
                  />
                  {errors.comment && touched.comment && (
                    <p className="text-red-500 text-sm mt-1">{errors.comment}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">Please provide constructive feedback about the student's performance and behavior.</p>
                </div>

                {/* Rating Sections */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-4 border-t">
                  {/* Psychomotor Domain */}
                  {commentFields.psychomotor.length > 0 && (
                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                      <h4 className="font-bold text-gray-800 mb-4 border-b pb-2">
                        <i className="fas fa-running mr-2 text-blue-600"></i> Psychomotor Skills
                      </h4>
                      <div className="space-y-4 max-h-80 overflow-y-auto pr-2">
                        {commentFields.psychomotor.map((field) => 
                          renderRatingField(
                            field, 
                            values.psycho_motor && values.psycho_motor[field.field_code] !== undefined 
                              ? values.psycho_motor[field.field_code] 
                              : 0,
                            (fieldCode, value) => {
                              const current = values.psycho_motor?.[fieldCode] || 0;
                              const newValue = current === value ? 0 : value;
                              setFieldValue(`psycho_motor.${fieldCode}`, newValue);
                            }
                          )
                        )}
                      </div>
                      {existingComment.has_psychomotor && (
                        <p className="text-xs text-green-600 mt-2">
                          <i className="fas fa-check-circle mr-1"></i> Ratings saved
                        </p>
                      )}
                    </div>
                  )}

                  {/* Affective Domain */}
                  {commentFields.affective.length > 0 && (
                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                      <h4 className="font-bold text-gray-800 mb-4 border-b pb-2">
                        <i className="fas fa-heart mr-2 text-red-600"></i> Affective Domain
                      </h4>
                      <div className="space-y-4 max-h-80 overflow-y-auto pr-2">
                        {commentFields.affective.map((field) => 
                          renderRatingField(
                            field, 
                            values.affective && values.affective[field.field_code] !== undefined 
                              ? values.affective[field.field_code] 
                              : 0,
                            (fieldCode, value) => {
                              const current = values.affective?.[fieldCode] || 0;
                              const newValue = current === value ? 0 : value;
                              setFieldValue(`affective.${fieldCode}`, newValue);
                            }
                          )
                        )}
                      </div>
                      {existingComment.has_affective && (
                        <p className="text-xs text-green-600 mt-2">
                          <i className="fas fa-check-circle mr-1"></i> Ratings saved
                        </p>
                      )}
                    </div>
                  )}

                  {/* Custom Domain */}
                  {commentFields.custom.length > 0 && (
                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                      <h4 className="font-bold text-gray-800 mb-4 border-b pb-2">
                        <i className="fas fa-cogs mr-2 text-green-600"></i> Custom Skills
                      </h4>
                      <div className="space-y-4 max-h-80 overflow-y-auto pr-2">
                        {commentFields.custom.map((field) => 
                          renderRatingField(
                            field, 
                            values.custom_ratings && values.custom_ratings[field.field_code] !== undefined 
                              ? values.custom_ratings[field.field_code] 
                              : 0,
                            (fieldCode, value) => {
                              const current = values.custom_ratings?.[fieldCode] || 0;
                              const newValue = current === value ? 0 : value;
                              setFieldValue(`custom_ratings.${fieldCode}`, newValue);
                            }
                          )
                        )}
                      </div>
                      {existingComment.has_custom && (
                        <p className="text-xs text-green-600 mt-2">
                          <i className="fas fa-check-circle mr-1"></i> Ratings saved
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Rating Summary */}
                <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                  <div className="flex items-center">
                    <i className="fas fa-info-circle text-yellow-600 mr-2"></i>
                    <span className="text-sm text-yellow-800">
                      <strong>Tip:</strong> Click on a star to rate (1-5). Click again to remove rating. 
                      Ratings are saved automatically with the comment.
                    </span>
                  </div>
                </div>

                {/* Debug Info (can be removed in production) */}
                {/* <div className="bg-gray-100 p-4 rounded-lg border border-gray-300">
                  <details>
                    <summary className="cursor-pointer font-medium text-gray-700">Debug Information</summary>
                    <div className="mt-2 text-xs">
                      <p><strong>Existing Comment:</strong> {JSON.stringify(existingComment)}</p>
                      <p><strong>Comment Fields:</strong> {JSON.stringify(commentFields)}</p>
                      <p><strong>Form Values:</strong> {JSON.stringify(values)}</p>
                    </div>
                  </details>
                </div> */}

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3 pt-6 border-t">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCommentModal(false);
                      setSelectedStudent(null);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <i className="fas fa-spinner fa-spin mr-2"></i>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save mr-2"></i>
                        {existingComment.has_comment ? 'Update' : 'Save'} Comment & Ratings
                      </>
                    )}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        )}
      </Modal>
    </div>
  );
};

export default ClassStudents;